import React, { useEffect, useState } from "react";
import { Modal, Table, Tag, Alert, Spin, Descriptions, Typography, Divider, Empty } from "antd";
import { produceApi } from "../../api/produceApi";
import { CostOverview, CostBreakdownLine, CostOverviewBatch } from "../../types/costOverview.type";
import { AverageCostSource } from "../../types/residue.type";
import { formatNumber, roundToThreeDecimals } from "../../utils/roundToThreeDecimals";
import dayjs from "dayjs";

const { Text } = Typography;

/** Человекочитаемое пояснение, откуда взялась средняя себестоимость. */
export const averageCostSourceLabel: Record<AverageCostSource, string> = {
  batches: "по партиям с остатком",
  lastBatch: "по последней партии (остатка нет)",
  manual: "ручная себестоимость",
  recipe: "по расчёту (партий не было)",
  none: "нет данных",
};

export const averageCostSourceColor: Record<AverageCostSource, string> = {
  batches: "green",
  lastBatch: "orange",
  manual: "blue",
  recipe: "purple",
  none: "default",
};

interface Props {
  produceId: string | null;
  produceName?: string;
  open: boolean;
  onClose: () => void;
}

export default function CostOverviewModal({ produceId, produceName, open, onClose }: Props) {
  const [data, setData] = useState<CostOverview | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !produceId) return;

    let cancelled = false;
    const fetchOverview = async () => {
      setLoading(true);
      setError(null);
      try {
        const overview = await produceApi.getCostOverview(produceId);
        if (!cancelled) setData(overview);
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.response?.data?.error || "Не удалось загрузить обзор себестоимости.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchOverview();
    return () => {
      cancelled = true;
    };
  }, [open, produceId]);

  // Сбрасываем данные при закрытии, чтобы не мигал прошлый продукт.
  useEffect(() => {
    if (!open) {
      setData(null);
      setError(null);
    }
  }, [open]);

  const totalUZS = data?.recipeCost.UZS || 0;

  const breakdownColumns = [
    {
      title: "Расходник",
      key: "name",
      render: (_: any, line: CostBreakdownLine) => (
        <div>
          <div>{line.name}</div>
          <div style={{ marginTop: 2 }}>
            <Tag color={line.kind === "material" ? "geekblue" : "cyan"}>
              {line.kind === "material" ? "Материал" : "Полуфабрикат"}
            </Tag>
            {line.isManual && <Tag color="blue">ручная себест.</Tag>}
            {line.source === "batch" && <Tag color="orange">из партий</Tag>}
            {line.missingPrice && <Tag color="red">нет цены</Tag>}
            {line.kind === "produce" && line.incomplete && !line.missingPrice && (
              <Tag color="red">неполный расчёт</Tag>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "Кол-во на 1 ед.",
      key: "quantity",
      align: "right" as const,
      render: (_: any, line: CostBreakdownLine) => (
        <span>
          {roundToThreeDecimals(line.quantity)} {line.unitOfMeasurement || ""}
        </span>
      ),
    },
    {
      title: "Цена за ед.",
      key: "unitCost",
      align: "right" as const,
      render: (_: any, line: CostBreakdownLine) => (
        <div>
          <div style={{ color: line.missingPrice ? "#cf1322" : undefined }}>
            {formatNumber(line.unitCost.UZS)} сум
          </div>
          <div style={{ fontSize: 12, color: "#888" }}>{formatNumber(line.unitCost.USD)} $</div>
        </div>
      ),
    },
    {
      title: "Сумма",
      key: "lineCost",
      align: "right" as const,
      render: (_: any, line: CostBreakdownLine) => (
        <div>
          <div style={{ fontWeight: 500 }}>{formatNumber(line.lineCost.UZS)} сум</div>
          <div style={{ fontSize: 12, color: "#888" }}>{formatNumber(line.lineCost.USD)} $</div>
        </div>
      ),
    },
    {
      title: "Доля",
      key: "share",
      align: "right" as const,
      render: (_: any, line: CostBreakdownLine) => {
        if (!totalUZS) return "—";
        const share = (line.lineCost.UZS / totalUZS) * 100;
        return <span>{share.toFixed(1)}%</span>;
      },
    },
  ];

  const batchColumns = [
    {
      title: "Дата",
      dataIndex: "date",
      key: "date",
      render: (date: string) => dayjs(date).format("DD.MM.YYYY HH:mm"),
    },
    {
      title: "Кол-во",
      dataIndex: "quantity",
      key: "quantity",
      align: "right" as const,
      render: (v: number) => roundToThreeDecimals(v),
    },
    {
      title: "Остаток",
      dataIndex: "residue",
      key: "residue",
      align: "right" as const,
      render: (v: number) => (
        <span style={{ color: v > 0 ? "#389e0d" : "#999" }}>{roundToThreeDecimals(v)}</span>
      ),
    },
    {
      title: "Себест. партии",
      key: "primeCost",
      align: "right" as const,
      render: (_: any, batch: CostOverviewBatch) => (
        <div>
          <div>{formatNumber(batch.primeCost?.UZS)} сум</div>
          <div style={{ fontSize: 12, color: "#888" }}>{formatNumber(batch.primeCost?.USD)} $</div>
        </div>
      ),
    },
    {
      title: "Учтена в средней",
      key: "counted",
      align: "center" as const,
      render: (_: any, batch: CostOverviewBatch) =>
        batch.residue > 0 ? <Tag color="green">да</Tag> : <Tag>нет</Tag>,
    },
  ];

  return (
    <Modal
      title={`Обзор себестоимости — ${data?.produce.name || produceName || ""}`}
      open={open}
      onCancel={onClose}
      footer={null}
      width={1000}
      destroyOnClose
    >
      {loading && (
        <div style={{ textAlign: "center", padding: 48 }}>
          <Spin size="large" />
        </div>
      )}

      {!loading && error && <Alert type="error" message={error} showIcon />}

      {!loading && !error && data && (
        <div>
          {/* 1. Итоги */}
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label="Средняя себест. (партии)">
              <div style={{ fontWeight: 600 }}>
                {formatNumber(data.averageCost.averageCostUZS)} сум
              </div>
              <div style={{ fontSize: 12, color: "#888" }}>
                {formatNumber(data.averageCost.averageCostUSD)} $
              </div>
              <Tag
                color={averageCostSourceColor[data.averageCost.source]}
                style={{ marginTop: 4 }}
              >
                {averageCostSourceLabel[data.averageCost.source]}
                {data.averageCost.source === "batches" &&
                  ` (${data.averageCost.batchCount} шт.)`}
              </Tag>
            </Descriptions.Item>

            <Descriptions.Item label="Себест. по расчёту (рецептура)">
              <div style={{ fontWeight: 600 }}>{formatNumber(data.recipeCost.UZS)} сум</div>
              <div style={{ fontSize: 12, color: "#888" }}>
                {formatNumber(data.recipeCost.USD)} $
              </div>
              {data.recipeCost.incomplete && (
                <Tag color="red" style={{ marginTop: 4 }}>
                  неполный расчёт
                </Tag>
              )}
              {data.breakdown.isManual && (
                <Tag color="blue" style={{ marginTop: 4 }}>
                  задана вручную
                </Tag>
              )}
            </Descriptions.Item>

            <Descriptions.Item label="Цена продажи">
              <div>{formatNumber(data.produce.price?.UZS)} сум</div>
              <div style={{ fontSize: 12, color: "#888" }}>
                {formatNumber(data.produce.price?.USD)} $
              </div>
            </Descriptions.Item>

            <Descriptions.Item label="Маржа к средней себест.">
              <span
                style={{
                  fontWeight: 600,
                  color:
                    data.averageCost.profitPercentageUZS >= 0 ? "#389e0d" : "#cf1322",
                }}
              >
                {data.averageCost.profitPercentageUZS.toFixed(2)}%
              </span>
              <div style={{ fontSize: 12, color: "#888" }}>
                остаток: {roundToThreeDecimals(data.produce.residue)}{" "}
                {data.produce.unitOfMeasurement}
              </div>
            </Descriptions.Item>
          </Descriptions>

          {/* Расхождение партийной и расчётной себестоимости */}
          {Math.abs(data.deviationPercentUZS) >= 5 && (
            <Alert
              style={{ marginTop: 12 }}
              type={Math.abs(data.deviationPercentUZS) >= 20 ? "error" : "warning"}
              showIcon
              message={`Расчёт по рецептуре расходится со средней по партиям на ${data.deviationPercentUZS.toFixed(
                2
              )}%`}
              description="Цены материалов изменились с момента последнего производства. Партийная себестоимость отражает прошлые закупки, расчётная — текущие цены."
            />
          )}

          {data.recipeCost.incomplete && data.recipeCost.missingMaterials.length > 0 && (
            <Alert
              style={{ marginTop: 12 }}
              type="warning"
              showIcon
              message="Расчёт неполный — нет цен на расходники"
              description={data.recipeCost.missingMaterials.slice(0, 10).join(", ")}
            />
          )}

          {/* 2. Расходники */}
          <Divider orientation="left" style={{ marginTop: 24 }}>
            Расходники и расчёт себестоимости
          </Divider>

          {data.breakdown.isManual ? (
            <Alert
              type="info"
              showIcon
              message="Себестоимость задана вручную"
              description="Для этой продукции указана ручная (нормативная) себестоимость — она заменяет расчёт по рецептуре, поэтому расшифровки расходников нет."
            />
          ) : data.breakdown.isLeaf ? (
            <Alert
              type="info"
              showIcon
              message="У продукции нет рецептуры"
              description="Себестоимость берётся из партий (закупка или производство), расшифровывать по расходникам нечего."
            />
          ) : data.breakdown.lines.length === 0 ? (
            <Empty description="Расходники не заданы" />
          ) : (
            <>
              <Table
                columns={breakdownColumns}
                dataSource={data.breakdown.lines}
                rowKey={(line) => `${line.kind}_${line.id}`}
                pagination={false}
                size="small"
                scroll={{ x: "max-content" }}
                summary={() => (
                  <Table.Summary fixed>
                    <Table.Summary.Row style={{ fontWeight: 600, background: "#fafafa" }}>
                      <Table.Summary.Cell index={0}>Итого</Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right" />
                      <Table.Summary.Cell index={2} align="right" />
                      <Table.Summary.Cell index={3} align="right">
                        <div>{formatNumber(data.recipeCost.UZS)} сум</div>
                        <div style={{ fontSize: 12, color: "#888" }}>
                          {formatNumber(data.recipeCost.USD)} $
                        </div>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={4} align="right">
                        100%
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                )}
              />

              {/* 3. Формула */}
              <div
                style={{
                  marginTop: 16,
                  padding: 12,
                  background: "#fafafa",
                  border: "1px solid #f0f0f0",
                  borderRadius: 4,
                }}
              >
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Как считается себестоимость по расчёту
                </Text>
                <div style={{ marginTop: 6 }}>
                  <Text>
                    Σ(материалы) {formatNumber(data.breakdown.materialsTotal.UZS)} сум +
                    Σ(полуфабрикаты) {formatNumber(data.breakdown.producesTotal.UZS)} сум ={" "}
                  </Text>
                  <Text strong>{formatNumber(data.recipeCost.UZS)} сум</Text>
                </div>
                <div style={{ marginTop: 4, fontSize: 12, color: "#888" }}>
                  Каждая строка = количество по рецептуре × текущая цена расходника.
                  Полуфабрикаты берутся по своей себестоимости (ручной, если задана, иначе
                  по их рецептуре).
                </div>
              </div>
            </>
          )}

          {/* 4. Партии */}
          <Divider orientation="left" style={{ marginTop: 24 }}>
            Партии (последние {data.batches.length})
          </Divider>

          {data.batches.length === 0 ? (
            <Empty description="Партий нет — продукция ещё не производилась и не закупалась" />
          ) : (
            <>
              <Table
                columns={batchColumns}
                dataSource={data.batches}
                rowKey={(batch) => batch._id}
                pagination={false}
                size="small"
                scroll={{ x: "max-content", y: 300 }}
              />
              <div style={{ marginTop: 8, fontSize: 12, color: "#888" }}>
                {data.averageCost.source === "batches" &&
                  "Средняя себестоимость — средневзвешенная по остаткам партий: Σ(остаток × себест. партии) ÷ Σ(остаток)."}
                {data.averageCost.source === "lastBatch" &&
                  "Остатка нет ни в одной партии, поэтому показана себестоимость последней партии по дате — цена продажи в себестоимость не подставляется."}
                {data.averageCost.source === "manual" &&
                  "Партий не было, поэтому показана ручная (нормативная) себестоимость."}
                {data.averageCost.source === "recipe" &&
                  "Партий не было, поэтому показан расчёт по рецептуре."}
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
